import type { Ref, WatchStopHandle } from 'vue';
import type {
  FieldHtmlNameType,
  HekateDisplayField,
  HekateDisplayForm,
} from '@onshape/www-inertia';
import { FieldHtmlNameUtil, Trans } from '@onshape/www-inertia';
import type UnifiedSignupSessionInfo from '../session-info/UnifiedSignupSessionInfo';
import ExternalUtil from '../../../../../ExternalUtil';
import type UnifiedSignupScreenComponent from '../UnifiedSignupScreenComponent';
import type { UnifiedSignupScreenName } from '../UnifiedSignupScreenName';
import TrackingUtil from '../../../../../util/TrackingUtil';
import type { SignupScreen } from './SignupScreen';
import VwoUtil from '~/hekate-www/external/util/VwoUtil';

export default abstract class AbstractSignupScreen implements SignupScreen {
  abstract readonly screenName: UnifiedSignupScreenName;

  protected readonly sessionInfo: Ref<UnifiedSignupSessionInfo>;

  protected readonly formDefinition: Ref<HekateDisplayForm>;

  private stopWatcherHandles: Array<WatchStopHandle>;

  protected static readonly signupPath: Array<UnifiedSignupScreenName> = [];

  public isBackButtonHidden: Ref<boolean>;

  constructor({
    sessionInfo,
    formDefinition,
    isBackButtonHidden,
  }: {
    sessionInfo: Ref<UnifiedSignupSessionInfo>;
    formDefinition: Ref<HekateDisplayForm>;
    isBackButtonHidden: Ref<boolean>;
  }) {
    this.sessionInfo = sessionInfo;
    this.formDefinition = formDefinition;
    this.stopWatcherHandles = [];
    this.isBackButtonHidden = isBackButtonHidden;
  }

  public abstract getVisibleComponents(): UnifiedSignupScreenComponent[];

  public abstract getValidatedComponents(): UnifiedSignupScreenComponent[];

  public abstract getProgress(): number;

  /** Return true if we should view this screen */
  public abstract predicate(): boolean;

  /** Called when the user clicks the next submit button */
  public abstract toNextScreen(): Promise<boolean>;

  public updateProgress(): void {
    const container = document.querySelector('.hekate-progress-bar') as HTMLDivElement;
    if (!container) {
      return;
    }

    const progressBarBox = container.querySelector('[role="progressbar"]') as HTMLDivElement;
    const progressBar = progressBarBox.querySelector('.progress-bar') as HTMLDivElement;
    if (this.getProgress() > 0) {
      container.classList.remove('d-none');
    } else {
      container.classList.add('d-none');
    }
    const oldValString = progressBarBox.getAttribute('aria-valuenow');
    const oldValue = oldValString ? parseInt(oldValString, 10) : 0;
    const newVal = oldValue < this.getProgress() ? this.getProgress() : oldValue + 10;

    progressBarBox.setAttribute('aria-valuenow', `${newVal}`);
    progressBar.style.width = `${newVal}%`;
  }

  public async toPreviousScreen(): Promise<boolean> {
    TrackingUtil.mouseFlowPush(['tag', 'uss-start-over']);
    document.location = document.location.href;
    return true;
  }

  public async initWatchers() {
    this.stopWatcherHandles = await this.getWatchers();
  }

  protected async getWatchers(): Promise<WatchStopHandle[]> {
    return [];
  }

  public async stopWatchers(): Promise<void> {
    this.stopWatcherHandles.forEach((sw) => sw());
  }

  public async initListeners(_params: { partialDiv: HTMLDivElement }): Promise<void> {
    /** NO-OP */
  }

  public getField(fieldName: FieldHtmlNameType): HekateDisplayField {
    return ExternalUtil.findDisplayFieldOrThrow({ formDefinition: this.formDefinition, fieldName });
  }

  public getNextButtonLabel(): string {
    return Trans.by('nextButton', 'Next');
  }

  public trackEvent(screenName?: UnifiedSignupScreenName): void {
    const currScreen = screenName || this.screenName;
    AbstractSignupScreen.signupPath.push(currScreen);
    TrackingUtil.mouseFlowPush(['tag', currScreen]);

    // push a conversion if there is a conversion for the current screen
    VwoUtil.vwoScreenGoalPush({ screenName: currScreen });

    const path = document.location.pathname;
    const cleanedPath = path[path.length - 1] === '/' ? path.slice(0, path.length - 1) : path;
    TrackingUtil.mouseFlowPush(['newPageView', `${cleanedPath}/${currScreen}`]);
    TrackingUtil.dataLayerPush({
      event: 'HekateSignupScreen',
      action: currScreen,
      ...AbstractSignupScreen.gaCleanPath(AbstractSignupScreen.signupPath),
    });
  }

  private static gaCleanPath(screenNames: UnifiedSignupScreenName[]): {
    signupPath1: string | undefined;
    signupPath2: string | undefined;
    signupPath3: string | undefined;
    signupPath4: string | undefined;
    signupPath5: string | undefined;
    signupPath6: string | undefined;
    signupPath7: string | undefined;
    signupPath8: string | undefined;
    signupPath9: string | undefined;
    signupPath10: string | undefined;
  } {
    const maxLengthForGaValue = 99;
    // one empty array for each `signupPathX`
    const results: Array<(string | undefined)[]> = [[], [], [], [], [], [], [], [], [], []];
    let resultsIndex = 0;
    const shortScreenNames = screenNames.map((sn) => sn.replace('uss-', ''));
    shortScreenNames.forEach((sn) => {
      if (resultsIndex >= results.length - 1) return;
      const curPath = results[resultsIndex]?.join(' ');
      const newLength = sn.length + (curPath?.length ?? 0);
      if (newLength >= maxLengthForGaValue) {
        resultsIndex += 1;
      }
      results[resultsIndex]?.push(sn);
    });

    const [
      signupPath1,
      signupPath2,
      signupPath3,
      signupPath4,
      signupPath5,
      signupPath6,
      signupPath7,
      signupPath8,
      signupPath9,
      signupPath10,
    ] = results.map((names) => names.join(' '));

    return {
      signupPath1,
      signupPath2,
      signupPath3,
      signupPath4,
      signupPath5,
      signupPath6,
      signupPath7,
      signupPath8,
      signupPath9,
      signupPath10,
    };
  }

  public isInitialScreen() {
    return false;
  }

  public isSubmitScreen() {
    return false;
  }

  /** Listen for hekate-input-field-name clicks to set field values and add/remove css classes */
  protected listenForFormInputs({
    partialDiv,
    fieldName,
  }: {
    partialDiv: HTMLDivElement;
    fieldName: FieldHtmlNameType;
  }) {
    const selector = `[hekate-input-field-name="${fieldName}"]`;
    const targets = partialDiv.querySelectorAll(selector);
    if (targets.length === 0) {
      throw new Error(`Could not find any listen inputs for selector ${selector}`);
    }
    targets.forEach((elm) => {
      elm.addEventListener('click', () => {
        targets.forEach((target) => {
          target.classList.remove('hekate-input-selected');
          target.classList.remove('hekate-input-highlighted');
        });
        const field = this.getField(fieldName);
        field.val = elm.getAttribute('hekate-input-value') as string;
        elm.classList.add('hekate-input-selected');
        elm.classList.add('hekate-input-highlighted');

        // if whatIsYourExperience, fill out personaDetail field
        if (fieldName === FieldHtmlNameUtil.whatIsYourExperience) {
          const personaHiddenField = this.getField(FieldHtmlNameUtil.personaDetail);
          const personaDetail = elm.querySelector('[hekate-persona-detail]');
          if (personaDetail) {
            personaHiddenField.val = personaDetail.getAttribute('hekate-persona-detail') as string;
          }
        }
      });
    });
    if (fieldName !== FieldHtmlNameUtil.chooseYourOnshapePlan) {
      return;
    }
    if (targets.length === 1) {
      (targets[0] as HTMLElement).click();
      return;
    }
    const hlPlans = Array.from(targets).find((plan) => plan.classList.contains('recommended'));
    if (hlPlans) {
      (hlPlans as HTMLElement).click();
    }
    return;
  }

  /** Listen for hekate-input-field-name Hover to add/remove css classes */
  protected listenForFormInputsHover({
    partialDiv,
    fieldName,
  }: {
    partialDiv: HTMLDivElement;
    fieldName: FieldHtmlNameType;
  }) {
    const selector = `[hekate-input-field-name="${fieldName}"]`;
    const targets = partialDiv.querySelectorAll(selector);
    if (targets.length === 0) {
      throw new Error(`Could not find any listen inputs for selector ${selector}`);
    }
    this.highlightTheSelectedOrDefault(targets);
    targets.forEach((elm) => {
      elm.addEventListener('mouseenter', () => {
        targets.forEach((target) => {
          target.classList.remove('hekate-input-highlighted');
        });
        elm.classList.add('hekate-input-highlighted');
      });
      elm.addEventListener('mouseleave', () => {
        elm.classList.remove('hekate-input-highlighted');
        this.highlightTheSelectedOrDefault(targets);
      });
    });
  }

  /** Add highlighted class to the selected elm or default. */

  private highlightTheSelectedOrDefault(targets: NodeListOf<Element>) {
    let isElmSelected = false;
    targets.forEach((target) => {
      if (target.classList.contains('hekate-input-selected')) {
        target.classList.add('hekate-input-highlighted');
        isElmSelected = true;
      }
    });
    if (!isElmSelected) {
      targets[0]?.classList.add('hekate-input-highlighted');
    }
  }

  public isOkEmailOrPro(): boolean {
    const { emailCheckCode, isProUpgrade } = this.sessionInfo.value;
    return emailCheckCode === 200 || isProUpgrade;
  }

  public isOkEmailOrEdu(): boolean {
    const { emailCheckCode, isEduRenew } = this.sessionInfo.value;
    return emailCheckCode === 200 || isEduRenew;
  }

  public isOkExistingUser(): boolean {
    const { emailCheckCode, isEduRenew, isProUpgrade } = this.sessionInfo.value;
    return emailCheckCode === 401 && (isEduRenew || isProUpgrade);
  }
}
